import React, { useEffect, useState } from 'react';
import { fetchPortalReturn, receivePortalJob, returnPortalJob, undoPortalReturn } from '../api';
import { intakeLabel } from './IncomingJobs';
import './incomingjobs.css';

const when = (iso) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const RETURN_WORDS = { dropped_off: 'Dropped off', picked_up: 'Customer picked up' };

// Top of a job the customer can follow on the commercial portal: one sent in there, or one
// entered here for a business that has the portal. While a portal job's wheels haven't
// arrived it has the Picked up / Received button. Once they're here, Dropped off and
// Customer picked up move it to the customer's Done list (nothing else changes), with Undo.
function PortalJobBanner({ job }) {
  const fromPortal = job.source === 'portal';
  const shown = fromPortal || Boolean(job.has_portal);
  const [state, setState] = useState(() => ({
    is_incoming: job.is_incoming,
    received_at: job.is_incoming ? null : job.received_at,
    returned_at: job.returned_at,
    returned_method: job.returned_method,
    stage: null,
    can_return: false, // until the server answers
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // The job came from a list that may be older than the last tap (here or on another
  // device), so read where it stands when the window opens.
  useEffect(() => {
    if (!shown) return undefined;
    let cancelled = false;
    fetchPortalReturn(job.id).then((fresh) => { if (!cancelled) setState(fresh); }).catch(() => {});
    return () => { cancelled = true; };
  }, [job.id, shown]);

  if (!shown) return null;

  const run = async (action) => {
    setBusy(true);
    setError('');
    try {
      await action();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not save. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const receive = () => run(async () => {
    const updated = await receivePortalJob(job.id);
    setState((s) => ({ ...s, is_incoming: false, received_at: updated.received_at }));
    window.dispatchEvent(new CustomEvent('portal-job-received', { detail: updated }));
    fetchPortalReturn(job.id).then(setState).catch(() => {});
  });
  const markReturned = (method) => run(async () => setState(await returnPortalJob(job.id, method)));
  const undo = () => run(async () => setState(await undoPortalReturn(job.id)));

  const pickup = job.intake_method === 'pickup';
  const waiting = state.is_incoming;
  // Archived while still waiting for its wheels: the customer sees it as cancelled.
  const cancelled = waiting && job.is_archived;
  const returned = Boolean(state.returned_at);
  // Archived before the business had the portal: it reads as returned there on its own.
  const returnedAsHistory = !returned && state.stage === 'returned';

  let status = '';
  if (cancelled) status = ' · Cancelled before the wheels came in (unarchive to bring it back)';
  else if (waiting) status = ' · Waiting for the wheels';
  else if (returned) status = ` · Returned: ${RETURN_WORDS[state.returned_method] || 'Returned'} ${when(state.returned_at)}`;
  else if (returnedAsHistory) status = ' · Shows as returned (archived before they had the portal)';
  else if (fromPortal && state.received_at) status = ` · ${pickup ? 'Picked up' : 'Received'} ${when(state.received_at)}`;

  return (
    <div className={`portal-banner ${waiting && !cancelled ? 'portal-banner-waiting' : ''}`}>
      <div className="portal-banner-text">
        <strong>{fromPortal ? 'Sent in on the customer portal' : 'On the customer portal'}</strong>
        {fromPortal && job.portal_submitted_by_name ? ` by ${job.portal_submitted_by_name}` : ''}
        {fromPortal && intakeLabel(job) ? ` · ${intakeLabel(job)}` : ''}
        {status}
        {error && <span className="portal-banner-error"> {error}</span>}
      </div>
      <div className="portal-banner-actions">
        {waiting && !cancelled && (
          <button type="button" className="incoming-receive" disabled={busy} onClick={receive}>
            {busy ? 'Saving…' : pickup ? 'Picked up' : 'Received'}
          </button>
        )}
        {!waiting && !returned && state.can_return && (
          <>
            <button type="button" className="incoming-receive" disabled={busy} onClick={() => markReturned('dropped_off')}>
              Dropped off
            </button>
            <button type="button" className="incoming-receive" disabled={busy} onClick={() => markReturned('picked_up')}>
              Customer picked up
            </button>
          </>
        )}
        {returned && (
          <button type="button" className="portal-banner-undo" disabled={busy} onClick={undo}>
            {busy ? 'Saving…' : 'Undo'}
          </button>
        )}
      </div>
    </div>
  );
}

export default PortalJobBanner;
